import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Hourglass, RefreshCw, Timer, Flag, AlertTriangle } from "lucide-react";
import { PRESETS, RangePicker } from "./BusinessProfitability";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const fmtDuration = (hours) => {
  if (hours === null || hours === undefined) return "—";
  if (hours < 1) return `${Math.max(Math.round(hours * 60), 0)} min`;
  if (hours < 48) {
    const h = Math.round(hours * 10) / 10;
    return `${h} ${h === 1 ? "hr" : "hrs"}`;
  }
  const d = Math.round((hours / 24) * 10) / 10;
  return `${d} ${d === 1 ? "day" : "days"}`;
};

const STAGE_TONES = {
  "Initial Contact": "bg-slate-400", Draft: "bg-gray-400", Sent: "bg-blue-500", "Changes Requested": "bg-orange-500",
  Approved: "bg-emerald-500", "In Production": "bg-amber-500", Complete: "bg-violet-500",
};
const nextLabel = (stage) => (stage === "Complete" ? "Paid" : null);

const StageRow = ({ s, max }) => (
  <div className="grid grid-cols-[150px_minmax(0,1fr)_90px_80px] items-center gap-3 py-2 border-t border-gray-100 text-sm" data-testid={`jd-stage-${s.stage.toLowerCase().replace(/\s+/g, "-")}`}>
    <span className="flex items-center gap-2 text-gray-700">
      <span className={`w-2 h-2 rounded-full ${STAGE_TONES[s.stage]}`} />
      {s.stage}{nextLabel(s.stage) && <span className="text-[11px] text-gray-400 flex items-center gap-0.5"><ArrowRight className="w-3 h-3" />Paid</span>}
    </span>
    <div className="h-2.5 rounded-full bg-gray-100 overflow-hidden">
      <div className={`h-full ${STAGE_TONES[s.stage]} transition-[width] duration-500`} style={{ width: s.average_hours ? `${Math.max((s.average_hours / max) * 100, 2)}%` : 0 }} />
    </div>
    <span className="font-mono text-right text-gray-900" data-testid="jd-stage-avg">{fmtDuration(s.average_hours)}</span>
    <span className="text-right text-[11px] text-gray-400" data-testid="jd-stage-jobs">{s.jobs} job{s.jobs === 1 ? "" : "s"}</span>
  </div>
);

const TotalCard = ({ icon: Icon, label, t, testId }) => (
  <div className="rounded-lg border border-gray-100 bg-white p-4" data-testid={testId}>
    <div className="flex items-center gap-1.5 text-xs text-gray-500"><Icon className="w-3.5 h-3.5" />{label}</div>
    <div className="mt-1.5 font-mono text-xl font-semibold text-gray-900" data-testid={`${testId}-value`}>{fmtDuration(t.average_hours)}</div>
    <div className="text-[11px] text-gray-400 mt-0.5" data-testid={`${testId}-jobs`}>Based on {t.jobs} job{t.jobs === 1 ? "" : "s"}</div>
  </div>
);

const SlowJobs = ({ jobs, minSample }) => (
  <div className="rounded-lg border border-gray-100 bg-white p-4" data-testid="jd-slow-jobs">
    <h3 className="text-sm font-medium text-gray-700 flex items-center gap-1.5 mb-1"><AlertTriangle className="w-4 h-4 text-amber-500" />Slower than average right now</h3>
    <p className="text-xs text-gray-400 mb-3">Active jobs sitting in their current stage longer than that stage's all-time average. A stage needs at least {minSample} measured jobs before jobs are flagged.</p>
    {jobs.length === 0 ? (
      <div className="text-sm text-gray-400 py-6 text-center" data-testid="jd-slow-empty">No active jobs are over their stage average.</div>
    ) : (
      <div className="max-h-[360px] overflow-y-auto">
        {jobs.map((j) => (
          <a key={j.id} href={`/quote?job=${j.id}`} target="_blank" rel="noopener noreferrer"
            className="grid grid-cols-[80px_minmax(0,1fr)_130px_100px_150px] items-center gap-3 py-2 border-t border-gray-100 text-sm hover:bg-gray-50 transition-colors" data-testid={`jd-slow-job-${j.id}`}>
            <span className="font-mono text-xs text-gray-500">{j.job_number}</span>
            <span className="truncate text-gray-800">{j.job_name || "Untitled"}{j.client_name && <span className="text-gray-400"> · {j.client_name}</span>}</span>
            <span className="flex items-center gap-1.5 text-xs text-gray-600"><span className={`w-2 h-2 rounded-full ${STAGE_TONES[j.stage]}`} />{j.stage}</span>
            <span className="font-mono text-right text-gray-900" title={j.since_go_live ? "Counted from when history tracking started; may have been longer" : ""}>
              {j.since_go_live ? "≥ " : ""}{fmtDuration(j.hours_in_stage)}
            </span>
            <span className="font-mono text-right text-red-600 text-xs whitespace-nowrap">+{fmtDuration(j.over_by_hours)} vs avg</span>
          </a>
        ))}
      </div>
    )}
  </div>
);

export default function JobDuration() {
  const [preset, setPreset] = useState("last_12");
  const [custom, setCustom] = useState({ from: "", to: "" });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const range = useMemo(() => (preset === "custom" ? [custom.from || null, custom.to || null] : PRESETS[preset].range()), [preset, custom]);

  const requestId = useRef(0);

  const load = async () => {
    const id = ++requestId.current;
    setLoading(true);
    try {
      const params = {};
      if (range[0]) params.start_date = range[0];
      if (range[1]) params.end_date = range[1];
      const res = await axios.get(`${API}/analytics/job-duration`, { params });
      if (id === requestId.current) setData(res.data);
    } catch (e) {
      if (id !== requestId.current) return;
      toast.error("Failed to load job duration");
      setData(null);
    } finally {
      if (id === requestId.current) setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range[0], range[1]]);

  const max = data ? Math.max(...data.stages.map((s) => s.average_hours || 0), 1) : 1;

  return (
    <div className="p-6" data-testid="job-duration">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-6">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2"><Hourglass className="w-5 h-5 text-blue-600" />Job Duration</h2>
          <p className="text-sm text-gray-500 mt-1 max-w-2xl">
            Average time jobs spend in each stage, from recorded history only (approximate backfilled times are excluded). Tasks are left out. The date range filters on when the time finished.
          </p>
        </div>
        <RangePicker preset={preset} setPreset={setPreset} custom={custom} setCustom={setCustom} onRefresh={load} loading={loading} idPrefix="jd" />
      </div>
      {loading && !data ? (
        <div className="text-center py-12 text-gray-400"><RefreshCw className="w-5 h-5 mx-auto mb-2 animate-spin" />Loading...</div>
      ) : data ? (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <TotalCard icon={Flag} label="Created → Complete" t={data.totals.created_to_complete} testId="jd-total-complete" />
            <TotalCard icon={Timer} label="Created → Invoice paid" t={data.totals.created_to_paid} testId="jd-total-paid" />
          </div>
          <div className="rounded-lg border border-gray-100 bg-white p-4" data-testid="jd-stages">
            <h3 className="text-sm font-medium text-gray-700 mb-1">Average time in each stage</h3>
            <div className="grid grid-cols-[150px_minmax(0,1fr)_90px_80px] gap-3 text-[11px] text-gray-400 pb-1">
              <span>Stage</span><span /><span className="text-right">Average</span><span className="text-right">Sample</span>
            </div>
            {data.stages.map((s) => <StageRow key={s.stage} s={s} max={max} />)}
          </div>
          <SlowJobs jobs={data.slow_jobs} minSample={data.slow_jobs_min_sample} />
        </div>
      ) : null}
    </div>
  );
}
